import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Minus, Plus } from "lucide-react";
import { toast } from "react-toastify";

import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { UnifiedModal } from "../../components/UnifiedModal";
import { formatDate, formatPrice, hoursPackPrice, scalePrice } from "./billingFormat";


// Compra de horas de incidencias: pago único en Stripe Checkout.
export const BuyHoursDialog = ({ hours, note = null, onClose, onCheckout }) => {
  const theme = useTheme();
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const unavailable = !hours.price;
  const total = unavailable ? null : hoursPackPrice(hours.price, quantity);

  useEffect(() => {
    if (!unavailable) return;
    toast.info("La compra de horas todavía no está disponible.");
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      if (!(await onCheckout(quantity))) setSaving(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "No se ha podido iniciar el pago. Inténtalo de nuevo.");
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  if (unavailable) return null;

  return (
    <UnifiedModal
      open
      onClose={handleClose}
      maxWidth="xs"
      title="Comprar horas"
      disableBackdropClose={saving}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Redirigiendo…" : "Ir al pago"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Cada incidencia consume horas de soporte técnico. Puedes comprar hasta {hours.price.packs.length} horas por compra; cuantas más compres, menos cuesta cada una.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outline"
            size="icon"
            aria-label="Una hora menos"
            disabled={saving || quantity <= 1}
            onClick={() => setQuantity((value) => value - 1)}
          >
            <Minus size={16} />
          </Button>
          <Box sx={{ minWidth: 96, textAlign: "center" }}>
            <Typography variant="h4" sx={{ fontWeight: 750, lineHeight: 1.1 }}>{quantity}</Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {quantity === 1 ? "hora" : "horas"}
            </Typography>
          </Box>
          <Button
            variant="outline"
            size="icon"
            aria-label="Una hora más"
            disabled={saving || quantity >= hours.price.packs.length}
            onClick={() => setQuantity((value) => value + 1)}
          >
            <Plus size={16} />
          </Button>
        </Box>

        <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 600 }}>
          Total: {formatPrice(total)}
          <Typography component="span" variant="caption" sx={{ display: "block", color: theme.palette.text.secondary }}>
            {formatPrice(scalePrice(total, 1 / quantity))} por hora
          </Typography>
        </Typography>

        <AlertNote variant="info">
          Es un pago único. Las horas se suman a tu saldo al confirmarse el pago y caducan el{" "}
          {formatDate(hours.expires_on)}.{note && ` ${note}`}
        </AlertNote>
        {error && <AlertNote variant="danger">{error}</AlertNote>}
      </Stack>
    </UnifiedModal>
  );
};

BuyHoursDialog.propTypes = {
  // El backend devuelve `price: null` sin precio configurado: quien lo abre debe comprobarlo antes
  hours: PropTypes.shape({
    price: PropTypes.object.isRequired,
    expires_on: PropTypes.string.isRequired,
  }).isRequired,
  note: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  // useStripeRedirect().go: false si la agencia se queda en la página
  onCheckout: PropTypes.func.isRequired,
};
